<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useToggle } from '@vueuse/core';

import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import VoiceCallButton from 'dashboard/components-next/Contacts/VoiceCallButton.vue';

// Contact detail shell (CrmB): white header with breadcrumb + actions over
// the grey canvas, then a 300px summary column and the main column.
const props = defineProps({
  selectedContact: {
    type: Object,
    default: () => ({}),
  },
  isUpdating: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['goToContactsList', 'toggleBlock', 'merge']);

const { t } = useI18n();
const route = useRoute();

const contactId = computed(() => route.params.contactId);
const isContactBlocked = computed(() => props.selectedContact?.blocked);

const [showMoreActions, toggleMoreActions] = useToggle();
const moreActionsRef = ref(null);

const moreActionItems = computed(() => [
  {
    label: t('CONTACTS_LAYOUT.DETAILS.MORE_ACTIONS.MERGE'),
    value: 'merge',
    action: 'merge',
    icon: 'i-lucide-merge',
  },
]);

const onMoreAction = ({ action }) => {
  toggleMoreActions(false);
  if (action === 'merge') emit('merge');
};
</script>

<template>
  <section class="flex flex-col w-full h-full min-h-0 overflow-hidden bg-n-background">
    <header
      class="flex items-center gap-3 px-6 py-4 border-b border-n-weak bg-n-solid-2"
    >
      <nav class="flex items-center gap-3 min-w-0 text-[12.5px] font-semibold">
        <button
          type="button"
          class="text-n-slate-10 hover:text-n-blue-text transition-colors"
          @click="emit('goToContactsList')"
        >
          {{ t('CONTACTS_LAYOUT.HEADER.BREADCRUMB.CONTACTS') }}
        </button>
        <span class="font-normal text-n-slate-8">/</span>
        <span class="truncate text-n-slate-11">
          {{ selectedContact?.name }}
        </span>
      </nav>

      <div class="flex items-center gap-2 ml-auto flex-shrink-0">
        <div
          ref="moreActionsRef"
          v-on-clickaway="() => toggleMoreActions(false)"
          class="relative"
        >
          <Button
            v-tooltip.bottom="t('CONTACTS_LAYOUT.DETAILS.MORE_ACTIONS.TITLE')"
            icon="i-lucide-ellipsis"
            color="slate"
            variant="outline"
            size="sm"
            class="w-8"
            @click="toggleMoreActions()"
          />
          <DropdownMenu
            v-if="showMoreActions"
            :menu-items="moreActionItems"
            class="z-20 w-52 mt-1.5 top-full ltr:right-0 rtl:left-0"
            @action="onMoreAction"
          />
        </div>
        <Button
          :label="
            !isContactBlocked
              ? t('CONTACTS_LAYOUT.HEADER.BLOCK_CONTACT')
              : t('CONTACTS_LAYOUT.HEADER.UNBLOCK_CONTACT')
          "
          size="sm"
          color="slate"
          variant="outline"
          :is-loading="isUpdating"
          :disabled="isUpdating"
          @click="emit('toggleBlock', isContactBlocked)"
        />
        <VoiceCallButton
          :phone="selectedContact?.phoneNumber"
          :contact-id="contactId"
          :label="t('CONTACT_PANEL.CALL')"
          size="sm"
        />
        <ComposeConversation :contact-id="contactId">
          <template #trigger="{ toggle }">
            <Button
              :label="t('CONTACTS_LAYOUT.HEADER.SEND_MESSAGE')"
              icon="i-lucide-message-square"
              size="sm"
              @click="toggle"
            />
          </template>
        </ComposeConversation>
      </div>
    </header>

    <main class="flex-1 min-h-0 overflow-y-auto">
      <div
        class="grid grid-cols-1 lg:grid-cols-[300px,minmax(0,1fr)] items-start gap-5 px-6 pt-5 pb-8"
      >
        <slot name="aside" />
        <div class="flex flex-col gap-4 min-w-0">
          <slot />
        </div>
      </div>
    </main>
  </section>
</template>
